import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { trialService } from '../../services/trialService';
import { getUserVideoCredits } from '../../services/videoCreditsService';
import toast from 'react-hot-toast';
import SocialAccountConnector from '../marketing/components/socialMediaPlanner/SocialAccountConnector';
import { businessService } from '../../services/businessService';
import type { Business } from '../../types/business';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import { getIcsUrl } from '../../services/crm/icsService';

interface Subscription {
  id: string;
  status: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  plan_name: string;
  plan_id: string; // e.g., 'monthly', 'entry_telemarketing', 'pro_telemarketing'
  price: string;
  interval: string;
}

// Structured business profile options for better data quality and AI personalization
const BUSINESS_STAGES = [
  { value: 'idea', label: 'Idea Stage', description: 'Concept validation, no revenue yet' },
  { value: 'mvp', label: 'MVP/Launch', description: 'First customers, testing product-market fit' },
  { value: 'growth', label: 'Growth', description: 'Scaling revenue, proven business model' },
  { value: 'mature', label: 'Mature', description: 'Established business, optimizing operations' },
  { value: 'pivot', label: 'Pivoting', description: 'Changing strategy or product direction' }
];

const INDUSTRIES = [
  'SaaS/Software',
  'E-commerce',
  'Professional Services',
  'Healthcare',
  'Education/EdTech',
  'FinTech',
  'MarTech',
  'Real Estate',
  'Manufacturing',
  'Retail',
  'Consulting',
  'Other'
];

const TARGET_MARKETS = [
  { value: 'b2b_smb', label: 'B2B SMB (1-50 employees)' },
  { value: 'b2b_mid', label: 'B2B Mid-market (50-500 employees)' },
  { value: 'b2b_enterprise', label: 'B2B Enterprise (500+ employees)' },
  { value: 'b2c_mass', label: 'B2C Mass Market' },
  { value: 'b2c_niche', label: 'B2C Niche/Premium' },
  { value: 'b2b2c', label: 'B2B2C/Platform' }
];

const GEOGRAPHIES = [
  { value: 'uk', label: 'United Kingdom' },
  { value: 'eu', label: 'European Union' },
  { value: 'us', label: 'United States' },
  { value: 'us_ca', label: 'US & Canada' },
  { value: 'global', label: 'Global/Multi-region' },
  { value: 'apac', label: 'Asia-Pacific' },
  { value: 'other', label: 'Other' }
];

const REVENUE_MODELS = [
  { value: 'subscription', label: 'Subscription/SaaS' },
  { value: 'transaction', label: 'Transaction/Commission' },
  { value: 'product', label: 'Product Sales' },
  { value: 'service', label: 'Service/Consulting' },
  { value: 'freemium', label: 'Freemium' },
  { value: 'advertising', label: 'Advertising' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'hybrid', label: 'Hybrid Model' }
];

const TEAM_SIZES = [
  { value: '1', label: 'Solo founder' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11-20', label: '11-20 people' },
  { value: '21+', label: '21+ people' }
];

const AccountPage: React.FC = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription' | 'security' | 'connections' | 'social' | 'business' | 'ecommerce' | 'files'>('profile');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [trialStatus, setTrialStatus] = useState<{
    isInTrial: boolean;
    daysRemaining: number;
    trialEnd: string | null;
  }>({ isInTrial: false, daysRemaining: 0, trialEnd: null });
  const [videoCredits, setVideoCredits] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(false);
  // CRM ICS
  const { activeOrgId } = useCrmOrgStore();
  const [icsUrl, setIcsUrl] = useState<string>('');
  const [icsLoading, setIcsLoading] = useState<boolean>(false);
  // Connections tab state
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState<Array<{ id: string; provider: string; email: string }>>([]);
  const [voices, setVoices] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  // WhatsApp linking state
  const [waCode, setWaCode] = useState<string | null>(null);
  const [waExpiresAt, setWaExpiresAt] = useState<string | null>(null);
  const [waConnected, setWaConnected] = useState<boolean>(false);
  const [waPolling, setWaPolling] = useState<boolean>(false);
  // Marketing report preferences
  const [reportFrequency, setReportFrequency] = useState<'weekly' | 'monthly' | 'never' | ''>('');
  const [reportPrefLoading, setReportPrefLoading] = useState(false);
  // Connections gating for marketing reports
  const [analyticsConnected, setAnalyticsConnected] = useState(false);
  const [websiteConnected, setWebsiteConnected] = useState(false);
  // IMAP modal state
  const [imapOpen, setImapOpen] = useState(false);
  const [imapSubmitting, setImapSubmitting] = useState(false);
  const [imapForm, setImapForm] = useState({
    email: '',
    name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    imap_host: '',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    smtp_secure: false,
    smtp_require_tls: true,
    smtp_tls_reject_unauthorized: true,
  });
  // Business Profile tab state
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [showCreateBiz, setShowCreateBiz] = useState(false);
  const [creatingBiz, setCreatingBiz] = useState(false);
  const [bizName, setBizName] = useState('');
  const [bizIndustry, setBizIndustry] = useState('');
  const [bizWebsite, setBizWebsite] = useState('');
  const [bizStage, setBizStage] = useState('');
  const [bizTargetCustomer, setBizTargetCustomer] = useState('');
  const [bizGeography, setBizGeography] = useState('');
  const [bizChannels, setBizChannels] = useState<string>('');
  const [bizGoals, setBizGoals] = useState<string>('');
  const [bizSetDefault, setBizSetDefault] = useState<boolean>(true);
  // New structured fields
  const [bizRevenueModel, setBizRevenueModel] = useState<string>('');
  const [bizTeamSize, setBizTeamSize] = useState<string>('');
  const [bizMonthlyRevenue, setBizMonthlyRevenue] = useState<string>('');
  const [bizUniqueValueProp, setBizUniqueValueProp] = useState<string>('');
  const [bizPainPoints, setBizPainPoints] = useState<string>('');
  // E-commerce (Shopify) tab state
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [shopifyStatus, setShopifyStatus] = useState<{ connected: boolean; shop_domain?: string; connected_at?: string } | null>(null);
  const [shopDomain, setShopDomain] = useState('');
  const [reports, setReports] = useState<Array<{ id: string; report_date: string; summary: string }>>([]);

  // HubSpot (CRM) state
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotStatus, setHubspotStatus] = useState<{ connected: boolean } | null>(null);
  const [hubspotReports, setHubspotReports] = useState<Array<{ id: string; report_date: string; summary: string; kpis?: any }>>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else if (user) {
      // Get user profile info
      fetchUserProfile();
      
      // Get subscription info
      fetchSubscriptionDetails();
      
      // Get trial status info
      fetchTrialStatus();

      // Get video credits balance
      fetchVideoCredits();
    }
  }, [isAuthenticated, user, navigate]);

  // Load connections data when the tab is opened
  useEffect(() => {
    if (activeTab === 'connections') {
      void loadConnections();
      void loadHubspotStatus();
      void loadHubspotReports();
      void loadReportPrefs();
    }
    if (activeTab === 'business') {
      (async () => {
        try {
          const list = await businessService.list();
          setBusinesses(list);
        } catch (e) {
          console.warn('Unable to load businesses:', e);
        }
      })();
    }
    if (activeTab === 'ecommerce') {
      void loadShopifyStatus();
      void loadShopifyReports();
    }
  }, [activeTab]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        console.log('No user ID available, using email from auth context');
        // If we have a user email from auth context but no profile, use that
        if (user?.email) {
          setEmail(user.email);
          return;
        }
        return;
      }
      
      // Improved Supabase query with better error checking
      console.log('Fetching user profile with ID:', user.id);
      
      let { data, error } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', user.id)
        .single();

      // Handle missing column error (42703) by retrying with only 'email'
      if (error && (error as any).code === '42703') {
        console.warn('Column missing on users table; retrying profile fetch with email only');
        const retry = await supabase
          .from('users')
          .select('email')
          .eq('id', user.id)
          .single();
        data = retry.data as any;
        error = retry.error as any;
      }

      if (error) {
        console.error('Supabase query error details:', error);
        // Fallback to auth context data if query fails
        if (user?.email) {
          setEmail(user.email);
        }
        throw error;
      }
      
      if (data) {
        console.log('User profile data retrieved successfully');
        // Name may not exist on this table; keep blank if absent
        setName((data as any).name || '');
        setEmail(data.email || user.email || '');
      } else {
        console.log('No user profile data found, using email from auth context');
        if (user?.email) {
          setEmail(user.email);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Unable to load profile data, but you can still use the application');
    } finally {
      setLoading(false);
    }
  };

  // ICS URL generation
  const handleGenerateIcs = async () => {
    try {
      if (!activeOrgId) { toast.error('Select an active CRM organization first'); return; }
      setIcsLoading(true);
      const url = await getIcsUrl(activeOrgId);
      setIcsUrl(url);
      toast.success('ICS URL generated');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to generate ICS URL');
    } finally {
      setIcsLoading(false);
    }
  };

  // ----- HubSpot (CRM) Handlers & Loaders -----
  const loadHubspotStatus = async () => {
    try {
      setHubspotLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/hubspot/status', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Backend returns { connected: boolean, ... }
        setHubspotStatus({ connected: Boolean(data?.connected) });
      } else {
        setHubspotStatus({ connected: false });
      }
    } catch (e) {
      setHubspotStatus({ connected: false });
    } finally {
      setHubspotLoading(false);
    }
  };

  const loadHubspotReports = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/hubspot/reports', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.reports)) {
          setHubspotReports(data.reports.map((r: any) => ({ id: r.id, report_date: r.report_date, summary: r.summary, kpis: r.kpis })));
        }
      }
    } catch {}
  };

  const handleHubspotConnect = async () => {
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Ask backend for OAuth URL then redirect
      const token = await getToken();
      if (!token) return;
      const res = await fetch(new URL('/api/hubspot/connect', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        toast.error('Unable to start HubSpot connection');
        return;
      }
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (e) {
      toast.error('Unable to start HubSpot connection');
    }
  };

  const handleHubspotDisconnect = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/hubspot/disconnect', base).toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('HubSpot disconnected');
        setHubspotStatus({ connected: false });
        setHubspotReports([]);
      } else {
        toast.error('Failed to disconnect HubSpot');
      }
    } catch (e) {
      toast.error('Failed to disconnect HubSpot');
    }
  };

  const handleHubspotGenerateReport = async () => {
    try {
      setHubspotLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/hubspot/reports/generate', base).toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('HubSpot weekly report generated');
        await loadHubspotReports();
      } else {
        toast.error('Failed to generate HubSpot report');
      }
    } catch (e) {
      toast.error('Failed to generate HubSpot report');
    } finally {
      setHubspotLoading(false);
    }
  };

  const handleShopifyGenerateReport = async () => {
    try {
      setShopifyLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/shopify/reports/generate', base).toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Weekly report generated');
        await loadShopifyReports();
      } else {
        const t = await res.text();
        console.error('Generate report failed', res.status, t);
        toast.error('Failed to generate report');
      }
    } catch (e) {
      toast.error('Failed to generate report');
    } finally {
      setShopifyLoading(false);
    }
  };

  // ----- Connections Tab Logic -----
  const loadConnections = async () => {
    try {
      setConnectionsLoading(true);
      const token = await getToken();
      if (!token) return;

      // Fetch linked email accounts (safe: handles 404/missing endpoints)
      try {
        const accountsRes = await fetch(new URL('/api/email/accounts', process.env.REACT_APP_API_URL || 'http://localhost:5000').toString(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (accountsRes.ok) {
          const data = await accountsRes.json();
          if (Array.isArray(data?.accounts)) {
            setEmailAccounts(data.accounts.map((a: any) => ({ id: a.id, provider: a.provider, email: a.email })));
          }
        }
      } catch (e) {
        // Silently ignore for MVP if endpoint not present
        console.debug('Email accounts endpoint not available yet');
      }

      // Fetch ElevenLabs voices (or fallback)
      try {
        const voicesRes = await fetch(new URL('/api/voice/voices', process.env.REACT_APP_API_URL || 'http://localhost:5000').toString(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (voicesRes.ok) {
          const data = await voicesRes.json();
          const vs = Array.isArray(data?.voices) ? data.voices : [];
          setVoices(vs.map((v: any) => ({ id: v.id, name: v.name })));
          if (!selectedVoiceId && vs[0]?.id) setSelectedVoiceId(vs[0].id);
        } else {
          setVoices([]);
        }
      } catch (e) {
        // Fallback placeholder voices so UI remains usable
        setVoices([
          { id: 'default', name: 'Default Voice' },
          { id: 'warm', name: 'Warm Narrator' },
        ]);
        if (!selectedVoiceId) setSelectedVoiceId('default');
      }

      // Fetch analytics connection status (Google/Bing)
      try {
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const url = new URL('/api/marketing/web-traffic-analytics', base);
        url.searchParams.set('dateRange', 'last_7_days');
        if (user?.id) url.searchParams.set('userId', user.id);
        const res = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const st = data?.accountStatus || {};
          const g = !!st.googleConnected;
          const b = !!st.bingConnected;
          setAnalyticsConnected(g || b);
        } else {
          setAnalyticsConnected(false);
        }
      } catch {
        setAnalyticsConnected(false);
      }

      // Determine if at least one website is connected via business profiles
      try {
        const list = await businessService.list();
        const hasSite = Array.isArray(list) && list.some((biz: any) => !!(biz?.website || biz?.url || biz?.domain));
        setWebsiteConnected(!!hasSite);
      } catch {
        setWebsiteConnected(false);
      }

      // Fetch WhatsApp connection status
      try {
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await fetch(new URL('/api/whatsapp/status', base).toString(), {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWaConnected(!!data?.connected);
        }
      } catch {}
    } finally {
      setConnectionsLoading(false);
    }
  };

  // ----- Marketing Report Preferences -----
  const loadReportPrefs = async () => {
    try {
      setReportPrefLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/marketing/report-prefs', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        // If 404/no pref, leave default and do not toast
        return;
      }
      const data = await res.json();
      const freq = (data?.frequency || 'never') as 'weekly' | 'monthly' | 'never';
      setReportFrequency(freq);
    } catch (e) {
      // Silent fail for MVP
      console.debug('Unable to load report prefs');
    } finally {
      setReportPrefLoading(false);
    }
  };

  const saveReportPref = async (freq: 'weekly' | 'monthly' | 'never') => {
    try {
      setReportPrefLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/marketing/report-prefs', base).toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ frequency: freq })
      });
      if (!res.ok) {
        const t = await res.text();
        console.error('Save report pref failed', res.status, t);
        toast.error('Failed to save preference');
        return;
      }
      setReportFrequency(freq);
      toast.success('Report preference saved');
    } catch (e) {
      toast.error('Unable to save preference');
    } finally {
      setReportPrefLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Redirect to backend OAuth start; backend will handle callback and token storage
      window.location.href = new URL('/api/email/connect/gmail', base).toString();
    } catch (e) {
      toast.error('Unable to start Gmail connection');
    }
  };

  const handleConnectMicrosoft = async () => {
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Redirect to backend OAuth start; backend will handle callback and token storage
      window.location.href = new URL('/api/email/connect/microsoft', base).toString();
    } catch (e) {
      toast.error('Unable to start Microsoft connection');
    }
  };

  const handleAddImap = async () => {
    setImapOpen(true);
  };

  const handleSubmitImap = async (e?: React.FormEvent) => {
    try {
      if (e) e.preventDefault();
      setImapSubmitting(true);
      // Basic validation
      const required: Array<keyof typeof imapForm> = ['email','smtp_host','smtp_port','smtp_username','smtp_password','imap_host','imap_port','imap_username','imap_password'];
      for (const k of required) {
        const v: any = (imapForm as any)[k];
        if (v === undefined || v === null || v === '') {
          toast.error('Please fill all required IMAP/SMTP fields');
          setImapSubmitting(false);
          return;
        }
      }
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/email/imap', base).toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          userId: user?.id,
          email: imapForm.email,
          name: imapForm.name || undefined,
          smtp_host: imapForm.smtp_host,
          smtp_port: Number(imapForm.smtp_port),
          smtp_username: imapForm.smtp_username,
          smtp_password: imapForm.smtp_password,
          imap_host: imapForm.imap_host,
          imap_port: Number(imapForm.imap_port),
          imap_secure: Number(imapForm.imap_port) === 993, // aligns with email_mailboxes schema
          imap_username: imapForm.imap_username,
          imap_password: imapForm.imap_password,
          smtp_secure: Boolean(imapForm.smtp_secure),
          smtp_require_tls: Boolean(imapForm.smtp_require_tls),
          smtp_tls_reject_unauthorized: Boolean(imapForm.smtp_tls_reject_unauthorized),
        })
      });
      if (!res.ok) {
        const t = await res.text();
        // Avoid logging full payloads or secrets if the server echoes them back
        const safeText = t.length > 500 ? t.slice(0, 500) + '…' : t;
        console.error('IMAP connect failed', res.status, safeText);
        toast.error('Failed to add IMAP account');
        setImapSubmitting(false);
        return;
      }
      const json = await res.json();
      const a = json?.account;
      if (a?.id) {
        setEmailAccounts(prev => [{ id: a.id, provider: a.provider, email: a.email }, ...prev]);
        toast.success('IMAP account connected');
      }
      setImapOpen(false);
    } catch (err) {
      toast.error('Unable to add IMAP account');
    } finally {
      setImapSubmitting(false);
    }
  };

  const handleDisconnectEmail = async (accountId: string) => {
    try {
      const token = await getToken();
      if (!token) return;

      const res = await fetch(new URL(`/api/email/accounts/${accountId}`, process.env.REACT_APP_API_URL || 'http://localhost:5000').toString(), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setEmailAccounts(prev => prev.filter(a => a.id !== accountId));
        toast.success('Disconnected');
      } else {
        toast.error('Failed to disconnect');
      }
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleConnectWhatsApp = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const url = new URL('/api/whatsapp/link-code', base);
      if (user?.id) url.searchParams.set('userId', user.id);
      const res = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Expect { linkUrl } or { code }
        if (data?.linkUrl) {
          window.open(data.linkUrl, '_blank');
        } else if (data?.code) {
          setWaCode(data.code);
          setWaExpiresAt(data.expiresAt || null);
          toast.success('WhatsApp link code generated');
          // Start short-lived polling to detect linking
          if (!waPolling) {
            setWaPolling(true);
            const started = Date.now();
            const poll = async () => {
              try {
                const sres = await fetch(new URL('/api/whatsapp/status', base).toString(), {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (sres.ok) {
                  const sdata = await sres.json();
                  if (sdata?.connected) {
                    setWaConnected(true);
                    setWaCode(null);
                    setWaExpiresAt(null);
                    toast.success('WhatsApp connected');
                    setWaPolling(false);
                    return; // stop polling
                  }
                }
              } catch {}
              if (Date.now() - started < 3 * 60 * 1000) { // up to 3 minutes
                setTimeout(poll, 5000);
              } else {
                setWaPolling(false);
              }
            };
            setTimeout(poll, 5000);
          }
        } else {
          toast('WhatsApp linking started. Follow instructions sent to your number.');
        }
      } else {
        const t = await res.text();
        console.error('Link code error', res.status, t);
        toast.error('Unable to generate WhatsApp link');
      }
    } catch (e) {
      toast.error('WhatsApp connection not available yet');
    }
  };

  const handleSaveVoice = async () => {
    try {
      const token = await getToken();
      if (!token || !selectedVoiceId) return;
      const res = await fetch(new URL('/api/voice/prefs', process.env.REACT_APP_API_URL || 'http://localhost:5000').toString(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ voiceId: selectedVoiceId })
      });
      if (res.ok) {
        toast.success('Voice preference saved');
      } else {
        toast.error('Failed to save voice');
      }
    } catch (e) {
      toast.error('Voice preference service unavailable');
    }
  };

  const fetchTrialStatus = async () => {
    try {
      if (!user?.id) return;
      
      const token = await getToken();
      if (!token) return;
      
      const status = await trialService.getTrialStatus(token, user.id);
      setTrialStatus({
        isInTrial: status.isInTrial,
        daysRemaining: status.daysRemaining,
        trialEnd: status.trialEnd
      });
    } catch (error) {
      console.error('Error fetching trial status:', error);
      // We don't show error toast for trial failures to avoid confusion
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        console.log('No user ID available for subscription fetch');
        return;
      }
      
      console.log('Fetching subscription details for user:', user.id);
      
      // Call backend API to get subscription details with enhanced error handling
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL(`/api/payment/subscription/${user.id}`, process.env.REACT_APP_API_URL || 'http://localhost:5000');
      
      try {
        const token = await getToken();
        if (!token) {
          console.error('No auth token available for subscription fetch');
          return;
        }
        
        let response = await fetch(baseUrl.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Check for specific error codes and handle appropriately
        if (response.status === 404) {
          console.log('No subscription found for user - normal for new users');
          return; // No subscription is a valid state, not an error
        } else if (response.status === 401 || response.status === 403) {
          console.error('Authentication error when fetching subscription');
          return;
        } else if (response.status === 400) {
          // Treat as no subscription (older/mismatched backends). Do not fallback to query-param route.
          console.log('Subscription endpoint returned 400; treating as no subscription for this user');
          return;
        } else if (!response.ok) {
          console.error(`Subscription fetch failed with status: ${response.status}`);
          throw new Error(`Failed to fetch subscription: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Subscription data retrieved successfully');
        
        // Validate subscription data before using it
        if (data.subscription) {
          // Check required fields exist
          if (!data.subscription.id || !data.subscription.status) {
            console.error('Incomplete subscription data received', data.subscription);
            return;
          }
          
          setSubscription({
            id: data.subscription.id,
            status: data.subscription.status,
            current_period_end: new Date(data.subscription.current_period_end * 1000).toLocaleDateString(),
            cancel_at_period_end: data.subscription.cancel_at_period_end || false,
            plan_name: data.subscription.plan?.name || 'Unknown Plan',
            plan_id: data.subscription.plan?.id || data.subscription.plan_id || 'monthly', // Get plan_id from API
            price: formatPrice(data.subscription.plan?.amount || 0, data.subscription.plan?.currency || 'GBP'),
            interval: data.subscription.plan?.interval || 'month'
          });
        }
      } catch (fetchError) {
        console.error('Network error when fetching subscription:', fetchError);
        // Handle network errors separately from response errors
      }
    } catch (error) {
      console.error('Error in subscription fetch logic:', error);
      // Only show an error toast if this is an unexpected error, not for missing subscriptions
      // Don't show error toast as user might not have a subscription yet
    } finally {
      setLoading(false);
    }
  };

  const getToken = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  };

  // ----- E-commerce (Shopify) Tab Logic -----
  const loadShopifyStatus = async () => {
    try {
      setShopifyLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/shopify/status', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setShopifyStatus({
          connected: !!data?.connected,
          shop_domain: data?.shop_domain,
          connected_at: data?.connected_at
        });
      } else {
        setShopifyStatus({ connected: false });
      }
    } catch (e) {
      setShopifyStatus({ connected: false });
    } finally {
      setShopifyLoading(false);
    }
  };

  const loadShopifyReports = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/shopify/reports', base).toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data?.reports)) {
          setReports(data.reports.map((r: any) => ({ id: r.id, report_date: r.report_date, summary: r.summary })));
        }
      }
    } catch (e) {
      // noop
    }
  };

  const handleShopifyConnect = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      if (!shopDomain || !shopDomain.endsWith('.myshopify.com')) {
        toast.error('Enter your full shop domain, e.g. mystore.myshopify.com');
        return;
      }
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Redirect to backend connect which issues install URL
      const url = new URL('/api/shopify/connect', base);
      url.searchParams.set('shop', shopDomain);
      window.location.href = url.toString();
    } catch (e) {
      toast.error('Unable to start Shopify connection');
    }
  };

  const handleShopifyDisconnect = async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/shopify/disconnect', base).toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Shopify disconnected');
        setShopifyStatus({ connected: false });
      } else {
        toast.error('Failed to disconnect');
      }
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleShopifySync = async () => {
    try {
      setShopifyLoading(true);
      const token = await getToken();
      if (!token) return;
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await fetch(new URL('/api/shopify/sync/daily', base).toString(), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Daily stats synced');
        await loadShopifyReports();
      } else {
        const t = await res.text();
        console.error('Sync failed', res.status, t);
        toast.error('Failed to sync');
      }
    } catch (e) {
      toast.error('Failed to sync');
    } finally {
      setShopifyLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    });
    return formatter.format(amount / 100);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('users')
        .update({ name })
        .eq('id', user.id);
        
      if (error) throw error;
      
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      if (!user?.id || !subscription?.id) return;
      
      // Call backend API to cancel subscription
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/subscription/cancel', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      // Update subscription state
      setSubscription({
        ...subscription,
        cancel_at_period_end: true
      });
      
      toast.success('Your subscription will be canceled at the end of the current billing period');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsCancelling(true);
      if (!user?.id || !subscription?.id) return;
      
      // Call backend API to reactivate subscription
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/subscription/reactivate', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }
      
      // Update subscription state
      setSubscription({
        ...subscription,
        cancel_at_period_end: false
      });
      
      toast.success('Your subscription has been reactivated');
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error('Failed to reactivate subscription');
    } finally {
      setIsCancelling(false);
    }
  };
  
  // Fetch user's video credit balance
  const fetchVideoCredits = async () => {
    try {
      setIsLoadingCredits(true);
      if (!user?.id) {
        return;
      }
      const response = await getUserVideoCredits(user.id);
      
      if (response.success && response.credits !== undefined) {
        setVideoCredits(response.credits);
      } else {
        console.error('Error fetching video credits:', response.error);
      }
    } catch (error) {
      console.error('Error fetching video credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };
  
  // Handle buying video credits
  const handleBuyCredits = async (creditPackage: string) => {
    try {
      setIsUpgrading(true);
      if (!user?.id || !email) return;
      
      // Use the checkout endpoint to create a checkout session for credits
      const baseUrl = new URL('/api/payment/credits-checkout', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          email: email,
          creditPackage: creditPackage // 'small', 'medium', or 'large'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating credits checkout session:', error);
      toast.error('Failed to start credit purchase. Please try again.');
      setIsUpgrading(false);
    }
  };
  
  // Handler for account upgrade using guest checkout endpoint
  const handleUpgradeAccount = async (planId: string) => {
    try {
      setIsUpgrading(true);
      if (!user?.id || !email) return;
      
      // Use the guest checkout endpoint to create a checkout session
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/guest-checkout', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          email: email, // Use the user's existing email
          planId: planId // 'monthly' or 'annual'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start upgrade process. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setLoading(true);
      if (!user?.id) return;
      
      // Call backend API to create a billing portal session
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/create-portal-session', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe billing portal
      window.location.href = url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to access billing portal');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToTelemarketing = async (planId: string) => {
    try {
      setIsUpgrading(true);
      const token = await getToken();
      if (!token || !user?.id) {
        toast.error('Authentication required');
        return;
      }
      
      // Call Stripe checkout with upgrade flow
      const response = await fetch(
        new URL(`/api/payment/checkout`, process.env.REACT_APP_API_URL || 'http://localhost:5000').toString(),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            planId,
            userId: user.id
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to create upgrade session');
      }
      
      const { url } = await response.json();
      window.location.href = url; // Redirect to Stripe
    } catch (error) {
      toast.error('Failed to start upgrade process');
      console.error('Error upgrading to telemarketing:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleChangePassword = () => {
    navigate('/reset-password');
  };

  const handleAccountDeletion = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setLoading(true);
        
        // Cancel subscription if active
        if (subscription && subscription.status === 'active' && !subscription.cancel_at_period_end) {
          await handleCancelSubscription();
        }
        
        // Delete user from Supabase
        const { error } = await supabase.auth.admin.deleteUser(user?.id || '');
        
        if (error) throw error;
        
        // Sign out after deletion
        await signOut();
        
        toast.success('Your account has been deleted');
        navigate('/');
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.error('Failed to delete account');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Trial Banner - only shown when user is in trial */}
      {subscription && subscription.status === 'trialing' && trialStatus.isInTrial && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md text-white">
          <div className="container mx-auto max-w-5xl">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold">Your trial ends in {trialStatus.daysRemaining} days!</h3>
                <p className="mt-1">Your card will be automatically charged on {subscription.current_period_end}.</p>
              </div>
              <div className="flex space-x-4">
                <button 
                  onClick={() => navigate('/payment')} 
                  className="px-6 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all"
                >
                  Manage Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="container mx-auto max-w-5xl">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">My Account</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage your account details and subscription.</p>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('profile')}
                className={`${activeTab === 'profile' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`${activeTab === 'subscription' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Subscription
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`${activeTab === 'security' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Security
              </button>
              <button
                onClick={() => setActiveTab('connections')}
                className={`${activeTab === 'connections' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Connections
              </button>
              <button
                onClick={() => setActiveTab('social')}
                className={`${activeTab === 'social' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Social
              </button>
              <button
                onClick={() => setActiveTab('business')}
                className={`${activeTab === 'business' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Business Profile
              </button>
              <button
                onClick={() => setActiveTab('ecommerce')}
                className={`${activeTab === 'ecommerce' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                E-commerce
              </button>
              <button
                onClick={() => setActiveTab('files')}
                className={`${activeTab === 'files' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Files
              </button>
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full name</label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm p-2"
                    />
                    <p className="mt-1 text-sm text-gray-500">Your email cannot be changed</p>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Business Profile Tab */}
            {activeTab === 'business' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-md font-semibold text-gray-900">Business Profiles</h4>
                    <p className="text-sm text-gray-500">Create a business profile to give the chatbot clear context.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCreateBiz(true)}
                    className="px-3 py-2 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 text-sm"
                  >
                    + Create business
                  </button>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  {businesses.length === 0 ? (
                    <p className="text-sm text-gray-500">No businesses yet.</p>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {businesses.map((b) => (
                        <li key={b.id} className="py-2 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{b.name}</p>
                            <p className="text-xs text-gray-500">{b.website || b.industry || '—'} {b.is_default ? '(default)' : ''}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Create Business Modal */}
                {showCreateBiz && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4">
                      <h3 className="text-lg font-bold mb-4">Create a business</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Business Name */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Business name *</label>
                          <input 
                            className="mt-1 w-full border rounded-md p-2" 
                            value={bizName} 
                            onChange={(e)=>setBizName(e.target.value)} 
                            placeholder="Acme Ltd" 
                            required 
                          />
                        </div>

                        {/* Industry Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Industry *</label>
                          <select 
                            className="mt-1 w-full border rounded-md p-2 bg-white"
                            value={bizIndustry}
                            onChange={(e)=>setBizIndustry(e.target.value)}
                            required
                          >
                            <option value="">Select industry...</option>
                            {INDUSTRIES.map(ind => (
                              <option key={ind} value={ind}>{ind}</option>
                            ))}
                          </select>
                        </div>

                        {/* Stage Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Stage *</label>
                          <select 
                            className="mt-1 w-full border rounded-md p-2 bg-white"
                            value={bizStage}
                            onChange={(e)=>setBizStage(e.target.value)}
                            required
                          >
                            <option value="">Select stage...</option>
                            {BUSINESS_STAGES.map(s => (
                              <option key={s.value} value={s.value}>
                                {s.label} - {s.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Target Market Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Target Market *</label>
                          <select 
                            className="mt-1 w-full border rounded-md p-2 bg-white"
                            value={bizTargetCustomer}
                            onChange={(e)=>setBizTargetCustomer(e.target.value)}
                            required
                          >
                            <option value="">Select market...</option>
                            {TARGET_MARKETS.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Geography Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Primary Geography *</label>
                          <select 
                            className="mt-1 w-full border rounded-md p-2 bg-white"
                            value={bizGeography}
                            onChange={(e)=>setBizGeography(e.target.value)}
                            required
                          >
                            <option value="">Select region...</option>
                            {GEOGRAPHIES.map(g => (
                              <option key={g.value} value={g.value}>{g.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Team Size Dropdown */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Team Size</label>
                          <select 
                            className="mt-1 w-full border rounded-md p-2 bg-white"
                            value={bizTeamSize}
                            onChange={(e)=>setBizTeamSize(e.target.value)}
                          >
                            <option value="">Select team size...</option>
                            {TEAM_SIZES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Website */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Website</label>
                          <input 
                            className="mt-1 w-full border rounded-md p-2" 
                            value={bizWebsite} 
                            onChange={(e)=>setBizWebsite(e.target.value)} 
                            placeholder="https://example.com" 
                            type="url"
                          />
                        </div>

                        {/* Conditional: Revenue Model (show for MVP+ stages) */}
                        {['mvp', 'growth', 'mature', 'pivot'].includes(bizStage) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Revenue Model</label>
                            <select 
                              className="mt-1 w-full border rounded-md p-2 bg-white"
                              value={bizRevenueModel}
                              onChange={(e)=>setBizRevenueModel(e.target.value)}
                            >
                              <option value="">Select model...</option>
                              {REVENUE_MODELS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Conditional: Monthly Revenue (show for growth+ stages) */}
                        {['growth', 'mature'].includes(bizStage) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Monthly Revenue (£)</label>
                            <input 
                              type="number"
                              className="mt-1 w-full border rounded-md p-2"
                              value={bizMonthlyRevenue}
                              onChange={(e)=>setBizMonthlyRevenue(e.target.value)}
                              placeholder="10000"
                              min="0"
                              step="100"
                            />
                          </div>
                        )}

                        {/* Main Channels */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Main Channels</label>
                          <input 
                            className="mt-1 w-full border rounded-md p-2" 
                            value={bizChannels} 
                            onChange={(e)=>setBizChannels(e.target.value)} 
                            placeholder="SEO, paid ads, partnerships, referrals" 
                          />
                        </div>

                        {/* Unique Value Proposition */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            What makes you different? (Unique Value Prop)
                          </label>
                          <textarea 
                            className="mt-1 w-full border rounded-md p-2" 
                            rows={2} 
                            value={bizUniqueValueProp} 
                            onChange={(e)=>setBizUniqueValueProp(e.target.value)} 
                            placeholder="e.g., Only GDPR-compliant HR tool built for UK SMBs with automated pension integration"
                          />
                        </div>

                        {/* Top Goals */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Top Goals (next 6 months)</label>
                          <textarea 
                            className="mt-1 w-full border rounded-md p-2" 
                            rows={2} 
                            value={bizGoals} 
                            onChange={(e)=>setBizGoals(e.target.value)} 
                            placeholder="e.g., reach £10k MRR, launch paid tier, raise seed round"
                          />
                        </div>

                        {/* Top 3 Pain Points */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Top 3 challenges you're facing
                          </label>
                          <textarea 
                            className="mt-1 w-full border rounded-md p-2" 
                            rows={3} 
                            value={bizPainPoints} 
                            onChange={(e)=>setBizPainPoints(e.target.value)} 
                            placeholder="1. Low conversion from trial to paid&#10;2. Struggling with SEO visibility&#10;3. High customer acquisition cost"
                          />
                        </div>

                        {/* Set as Default */}
                        <div className="md:col-span-2">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input type="checkbox" checked={bizSetDefault} onChange={(e)=>setBizSetDefault(e.target.checked)} />
                            Set as default business profile
                          </label>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                          onClick={() => { if (!creatingBiz) setShowCreateBiz(false); }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                          disabled={creatingBiz || !bizName.trim()}
                          onClick={async () => {
                            if (!bizName.trim()) return;
                            // Validate required fields
                            if (!bizIndustry || !bizStage || !bizTargetCustomer || !bizGeography) {
                              toast.error('Please fill in all required fields (*)');
                              return;
                            }
                            try {
                              setCreatingBiz(true);
                              const created = await businessService.create({
                                name: bizName,
                                industry: bizIndustry || undefined,
                                website: bizWebsite || undefined,
                                stage: bizStage || undefined,
                                target_customer: bizTargetCustomer || undefined,
                                geography: bizGeography || undefined,
                                main_channels: bizChannels || undefined,
                                goals: bizGoals || undefined,
                                is_default: bizSetDefault,
                                // New fields
                                revenue_model: bizRevenueModel || undefined,
                                team_size: bizTeamSize || undefined,
                                monthly_revenue: bizMonthlyRevenue ? parseFloat(bizMonthlyRevenue) : undefined,
                                unique_value_prop: bizUniqueValueProp || undefined,
                                pain_points: bizPainPoints ? bizPainPoints.split('\n').filter(p => p.trim()) : undefined,
                                completed_onboarding: true, // Mark as completed since they filled the form
                              } as any);
                              try {
                                const list = await businessService.list();
                                setBusinesses(list);
                              } catch {}
                              setShowCreateBiz(false);
                              // Reset all form fields
                              setBizName('');
                              setBizIndustry('');
                              setBizWebsite('');
                              setBizStage('');
                              setBizTargetCustomer('');
                              setBizGeography('');
                              setBizChannels('');
                              setBizGoals('');
                              setBizSetDefault(true);
                              setBizRevenueModel('');
                              setBizTeamSize('');
                              setBizMonthlyRevenue('');
                              setBizUniqueValueProp('');
                              setBizPainPoints('');
                              toast.success('Business profile created successfully');
                            } catch (e: any) {
                              console.error('Create business failed:', e);
                              toast.error(e?.message || 'Failed to create business');
                            } finally {
                              setCreatingBiz(false);
                            }
                          }}
                        >
                          {creatingBiz ? 'Creating...' : 'Create business'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* E-commerce (Shopify) Tab */}
            {activeTab === 'ecommerce' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-md font-semibold text-gray-900">Shopify</h4>
                    <p className="text-sm text-gray-500">Connect your Shopify store to see daily stats and AI weekly reports.</p>
                  </div>
                  {shopifyStatus?.connected ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Connected</span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Not Connected</span>
                  )}
                </div>

                {!shopifyStatus?.connected ? (
                  <form onSubmit={handleShopifyConnect} className="bg-gray-50 rounded p-4 space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Shop domain</label>
                    <div className="flex gap-2 max-w-xl">
                      <input
                        type="text"
                        placeholder="mystore.myshopify.com"
                        value={shopDomain}
                        onChange={(e) => setShopDomain(e.target.value.trim())}
                        className="flex-1 border border-gray-300 rounded-md p-2"
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Connect
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">You'll be redirected to Shopify to install the Explanatum app and grant permissions.</p>
                  </form>
                ) : (
                  <div className="bg-gray-50 rounded p-4">
                    <p className="text-sm text-gray-700">
                      Connected store: <span className="font-medium">{shopifyStatus?.shop_domain}</span>
                      {shopifyStatus?.connected_at && (
                        <span className="text-gray-500"> • since {new Date(shopifyStatus.connected_at).toLocaleString()}</span>
                      )}
                    </p>
                    <div className="mt-3 flex gap-3">
                      <button
                        type="button"
                        onClick={handleShopifySync}
                        disabled={shopifyLoading}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                      >
                        {shopifyLoading ? 'Syncing…' : 'Sync Daily Stats'}
                      </button>
                      <button
                        type="button"
                        onClick={handleShopifyGenerateReport}
                        disabled={shopifyLoading}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {shopifyLoading ? 'Working…' : 'Generate Weekly Report'}
                      </button>
                      <button
                        type="button"
                        onClick={handleShopifyDisconnect}
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Recent AI Reports</h5>
                  {reports.length === 0 ? (
                    <p className="text-sm text-gray-500">No reports yet. After syncing, weekly reports will appear here.</p>
                  ) : (
                    <ul className="divide-y divide-gray-200 bg-white border rounded">
                      {reports.map((r) => (
                        <li key={r.id} className="p-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-800">{new Date(r.report_date).toLocaleDateString()}</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{r.summary}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

              </div>
            )}

            {/* Files Tab */}
            {activeTab === 'files' && (
              <div className="space-y-6">
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="text-base font-medium text-gray-900">File Storage</h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Manage up to 10GB of files with a 50MB per-file upload limit. Your workspace includes preset folders for
                    <span className="font-medium"> Branding</span>, <span className="font-medium"> Social Media Images</span>, and <span className="font-medium"> Legal Documents</span>.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/tools/files')}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Open File Manager
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Connections Tab */}
            {activeTab === 'connections' && (
              <div className="space-y-8">
                {/* Email connections */}
                <section>
                  <h4 className="text-md font-semibold text-gray-900">Email accounts</h4>
                  <p className="text-sm text-gray-500 mb-3">Connect Gmail or Microsoft (OAuth), or add a generic IMAP/SMTP account.</p>
                  <div className="flex gap-3 mb-4">
                    <button
                      type="button"
                      onClick={handleConnectGmail}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Connect Gmail
                    </button>
                    <button
                      type="button"
                      onClick={handleConnectMicrosoft}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Connect Microsoft
                    </button>
                    <button
                      type="button"
                      onClick={handleAddImap}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Add IMAP Account
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-md p-3">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Linked accounts</h5>
                    {connectionsLoading ? (
                      <p className="text-sm text-gray-500">Loading...</p>
                    ) : emailAccounts.length === 0 ? (
                      <p className="text-sm text-gray-500">No accounts linked yet.</p>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {emailAccounts.map(acc => (
                          <li key={acc.id} className="py-2 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{acc.email}</p>
                              <p className="text-xs text-gray-500">{acc.provider}</p>
                            </div>
                            <button
                              onClick={() => handleDisconnectEmail(acc.id)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Disconnect
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                {/* WhatsApp connection */}
                <section>
                  <h4 className="text-md font-semibold text-gray-900">WhatsApp</h4>
                  <p className="text-sm text-gray-500 mb-3">Link your WhatsApp to receive and send messages.</p>
                  <button
                    type="button"
                    onClick={handleConnectWhatsApp}
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Connect WhatsApp
                  </button>
                  {waCode && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-900">
                      <p className="font-medium">Your link code:</p>
                      <p className="text-2xl font-mono tracking-wider mt-1">{waCode}</p>
                      <ul className="list-disc ml-5 mt-2 space-y-1">
                        <li>Open WhatsApp on your phone that joined the Twilio Sandbox.</li>
                        <li>Send the code above to the Sandbox number: <span className="font-semibold">+1 415 523 8886</span>.</li>
                        <li>If prompted, reply <span className="font-semibold">AGREE</span> to accept terms.</li>
                        <li>Then send a normal message (e.g. "Hi") to start chatting.</li>
                      </ul>
                      {waExpiresAt && (
                        <p className="mt-2 text-xs text-green-800">Expires at: {new Date(waExpiresAt).toLocaleString()}</p>
                      )}
                    </div>
                  )}
                </section>

                {/* CRM Calendar (ICS) */}
                <section>
                  <h4 className="text-md font-semibold text-gray-900">CRM Calendar (ICS)</h4>
                  <p className="text-sm text-gray-500 mb-3">Subscribe to your CRM meetings and tasks in your calendar app.</p>
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      type="button"
                      onClick={handleGenerateIcs}
                      disabled={icsLoading}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                    >
                      {icsLoading ? 'Generating…' : 'Generate ICS URL'}
                    </button>
                    {activeOrgId ? (
                      <span className="text-xs text-gray-600">Org: <span className="font-mono">{activeOrgId}</span></span>
                    ) : (
                      <span className="text-xs text-gray-500">Select an active CRM organization above</span>
                    )}
                  </div>
                  {icsUrl && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">ICS URL</label>
                      <div className="flex gap-2">
                        <input className="flex-1 border rounded p-2 text-xs" value={icsUrl} readOnly />
                        <button
                          type="button"
                          className="px-3 py-2 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 text-sm"
                          onClick={() => { navigator.clipboard.writeText(icsUrl); toast.success('Copied'); }}
                        >
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Use “Add calendar by URL” in Google Calendar/Outlook/Apple Calendar and paste this link.</p>
                    </div>
                  )}
                </section>

                {/* HubSpot (CRM) connection */}
                <section>
                  <h4 className="text-md font-semibold text-gray-900">HubSpot</h4>
                  <p className="text-sm text-gray-500 mb-3">Connect your HubSpot account to receive weekly CRM KPI reports.</p>
                  {hubspotStatus?.connected ? (
                    <div className="bg-gray-50 rounded p-4">
                      <p className="text-sm text-gray-700">Status: <span className="font-medium">Connected</span></p>
                      <div className="mt-3 flex gap-3">
                        <button
                          type="button"
                          onClick={handleHubspotGenerateReport}
                          disabled={hubspotLoading}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {hubspotLoading ? 'Working…' : 'Generate Weekly Report'}
                        </button>
                        <button
                          type="button"
                          onClick={handleHubspotDisconnect}
                          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Disconnect
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <button
                        type="button"
                        onClick={handleHubspotConnect}
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Connect HubSpot
                      </button>
                    </div>
                  )}

                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Recent AI Reports</h5>
                    {hubspotReports.length === 0 ? (
                      <p className="text-sm text-gray-500">No reports yet.</p>
                    ) : (
                      <ul className="divide-y divide-gray-200 bg-white border rounded">
                        {hubspotReports.map((r) => (
                          <li key={r.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-800">{new Date(r.report_date).toLocaleDateString()}</p>
                            </div>
                            <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{r.summary}</p>
                            {r.kpis && (
                              <div className="mt-2 text-xs text-gray-700">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  <div>Contacts: <span className="font-medium">{r.kpis.contacts_new ?? 0}</span></div>
                                  <div>Deals created: <span className="font-medium">{r.kpis.deals_created ?? 0}</span></div>
                                  <div>Deals won: <span className="font-medium">{r.kpis.deals_won ?? 0}</span></div>
                                  <div>Deals lost: <span className="font-medium">{r.kpis.deals_lost ?? 0}</span></div>
                                  <div>Pipeline: <span className="font-medium">{(r.kpis.pipeline_value ?? 0).toFixed ? r.kpis.pipeline_value.toFixed(2) : r.kpis.pipeline_value}</span></div>
                                  <div>Activities: <span className="font-medium">{r.kpis.activities_total ?? 0}</span></div>
                                  <div>• Calls: {r.kpis.activities_calls ?? 0}</div>
                                  <div>• Emails: {r.kpis.activities_emails ?? 0}</div>
                                  <div>• Meetings: {r.kpis.activities_meetings ?? 0}</div>
                                  <div>• Tasks: {r.kpis.activities_tasks ?? 0}</div>
                                  <div>• Notes: {r.kpis.activities_notes ?? 0}</div>
                                </div>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>

                {/* Marketing Report Frequency */}
                {subscription?.status === 'active' && analyticsConnected && websiteConnected && (
                  <section>
                    <h4 className="text-md font-semibold text-gray-900">Marketing report frequency</h4>
                    <p className="text-sm text-gray-500 mb-3">Choose how often you want AI-generated marketing summaries sent and saved.</p>
                    <div className="flex items-center gap-3">
                      <select
                        className="mt-1 block w-64 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                        value={reportFrequency || ''}
                        onChange={(e) => saveReportPref(e.target.value as 'weekly' | 'monthly' | 'never')}
                        disabled={reportPrefLoading}
                      >
                        <option value="" disabled>{reportPrefLoading ? 'Loading…' : 'Select frequency'}</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="never">Never</option>
                      </select>
                      {reportPrefLoading && (
                        <span className="text-xs text-gray-500">Saving…</span>
                      )}
                    </div>
                  </section>
                )}

                {/* ElevenLabs voice selection */}
                <section>
                  <h4 className="text-md font-semibold text-gray-900">Voice preferences</h4>
                  <p className="text-sm text-gray-500 mb-3">Choose your ElevenLabs voice for readouts and calls.</p>
                  <div className="flex items-center gap-3">
                    <select
                      className="mt-1 block w-64 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                      value={selectedVoiceId}
                      onChange={(e) => setSelectedVoiceId(e.target.value)}
                    >
                      {voices.length === 0 ? (
                        <option value="">No voices available</option>
                      ) : (
                        voices.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))
                      )}
                    </select>
                    <button
                      type="button"
                      onClick={handleSaveVoice}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </section>
              </div>
            )}
            
            {/* Social Tab - mirrors SocialMediaPlanner SocialAccountConnector */}
            {activeTab === 'social' && (
              <div className="space-y-8">
                <section>
                  <h4 className="text-md font-semibold text-gray-900 mb-2">Social accounts</h4>
                  <p className="text-sm text-gray-500 mb-4">Connect Facebook, LinkedIn, and Instagram. Accounts already connected will show their status.</p>
                  <div className="bg-white rounded-md border">
                    <SocialAccountConnector />
                  </div>
                </section>
              </div>
            )}
            
            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                {subscription ? (
                  <>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Current Plan</h4>
                          <p className="mt-1 text-lg font-semibold">{subscription.plan_name}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Status</h4>
                          <div className="mt-1 flex items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              subscription.status === 'active' ? 'bg-green-100 text-green-800' : 
                              subscription.status === 'trialing' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {subscription.status === 'active' ? 'Active' : 
                               subscription.status === 'trialing' ? `Trial (${trialStatus.daysRemaining} days left)` : 
                               subscription.status}
                            </span>
                            {subscription.cancel_at_period_end && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Cancels at period end
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Price</h4>
                          <p className="mt-1 text-lg font-semibold">{subscription.price} / {subscription.interval}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Current Period Ends</h4>
                          <p className="mt-1">{subscription.current_period_end}</p>
                          {subscription.status === 'trialing' && trialStatus.isInTrial && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">
                              Your card will be automatically charged on this date unless you cancel before the trial ends.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Telemarketing Upgrade Options - Show if NOT on telemarketing plan */}
                    {subscription.status === 'active' && !['entry_telemarketing', 'pro_telemarketing', 'enterprise_telemarketing'].includes(subscription.plan_id) && (
                      <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-lg p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          🚀 Unlock AI Telemarketing
                        </h3>
                        <p className="text-gray-700 mb-4">
                          Add AI-powered cold calling to your Explanatum subscription
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Entry Plan Card */}
                          <div className="bg-white p-4 rounded-lg border-2 border-purple-300">
                            <h4 className="font-bold text-lg mb-2 text-purple-700">Entry Plan</h4>
                            <div className="text-3xl font-bold text-purple-600 mb-2">£299<span className="text-sm text-gray-600">/mo</span></div>
                            <ul className="text-sm space-y-1 mb-4 text-gray-700">
                              <li>✓ 3 additional user licenses</li>
                              <li>✓ 500 AI call minutes/month</li>
                              <li>✓ All Explanatum features</li>
                              <li>✓ Add more: +£299 (3 users + 500 min)</li>
                            </ul>
                            <button
                              onClick={() => handleUpgradeToTelemarketing('entry_telemarketing')}
                              disabled={isUpgrading}
                              className="w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isUpgrading ? 'Processing...' : 'Upgrade to Entry'}
                            </button>
                            <p className="mt-2 text-xs text-gray-500 text-center">No free trial • Billed immediately</p>
                          </div>
                          
                          {/* Pro Plan Card */}
                          <div className="bg-white p-4 rounded-lg border-2 border-indigo-400 relative">
                            <div className="absolute -top-2 -right-2 bg-indigo-500 text-white px-2 py-1 rounded text-xs font-bold">
                              POPULAR
                            </div>
                            <h4 className="font-bold text-lg mb-2 text-indigo-700">Pro Plan</h4>
                            <div className="text-3xl font-bold text-indigo-600 mb-2">£899<span className="text-sm text-gray-600">/mo</span></div>
                            <ul className="text-sm space-y-1 mb-4 text-gray-700">
                              <li>✓ 3 additional user licenses</li>
                              <li>✓ 2,000 AI call minutes/month</li>
                              <li>✓ Priority support</li>
                              <li>✓ Add more: +£799 (3 users + 2,000 min)</li>
                            </ul>
                            <button
                              onClick={() => handleUpgradeToTelemarketing('pro_telemarketing')}
                              disabled={isUpgrading}
                              className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isUpgrading ? 'Processing...' : 'Upgrade to Pro'}
                            </button>
                            <p className="mt-2 text-xs text-gray-500 text-center">No free trial • Billed immediately</p>
                          </div>
                          
                          {/* Enterprise Plan Card */}
                          <div className="bg-white p-4 rounded-lg border-2 border-emerald-400 relative">
                            <div className="absolute -top-2 -right-2 bg-emerald-500 text-white px-2 py-1 rounded text-xs font-bold">
                              HIGH VOLUME
                            </div>
                            <h4 className="font-bold text-lg mb-2 text-emerald-700">Enterprise</h4>
                            <div className="text-3xl font-bold text-emerald-600 mb-2">£3,995<span className="text-sm text-gray-600">/mo</span></div>
                            <ul className="text-sm space-y-1 mb-4 text-gray-700">
                              <li>✓ 10 additional user licenses</li>
                              <li>✓ 10,000 AI call minutes/month</li>
                              <li>✓ Dedicated account manager</li>
                              <li>✓ Add more: +£3,495 (10 users + 10,000 min)</li>
                            </ul>
                            <button
                              onClick={() => handleUpgradeToTelemarketing('enterprise_telemarketing')}
                              disabled={isUpgrading}
                              className="w-full py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isUpgrading ? 'Processing...' : 'Upgrade to Enterprise'}
                            </button>
                            <p className="mt-2 text-xs text-gray-500 text-center">No free trial • Billed immediately</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Upgrade/Downgrade Options for Cold Email Pro */}
                    {subscription.status === 'active' && (
                      <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Upgrade Your Plan</h4>
                        
                        {!subscription.plan_name.includes('Cold Email Pro') && (
                          <>
                            <p className="text-sm text-gray-700 mb-4">
                              Get more contacts and email sending power with Cold Email Pro
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                              <div>
                                <p className="font-medium text-gray-900">Current Plan:</p>
                                <p className="text-gray-600">5,000 contacts</p>
                                <p className="text-gray-600">30,000 emails/month</p>
                              </div>
                              <div>
                                <p className="font-medium text-purple-700">Cold Email Pro:</p>
                                <p className="text-gray-600"><strong>30,000 contacts</strong> (6x more)</p>
                                <p className="text-gray-600"><strong>150,000 emails/month</strong> (5x more)</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUpgradeAccount('cold_email_pro')}
                              disabled={isUpgrading}
                              className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            >
                              {isUpgrading ? 'Processing...' : 'Upgrade to Cold Email Pro - £50/month'}
                            </button>
                          </>
                        )}
                        
                        {subscription.plan_name.includes('Cold Email Pro') && (
                          <>
                            <p className="text-sm text-gray-700 mb-4">
                              You're on our most powerful plan! Want to downgrade to the standard Premium plan?
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                              <div>
                                <p className="font-medium text-purple-700">Current: Cold Email Pro</p>
                                <p className="text-gray-600">30,000 contacts</p>
                                <p className="text-gray-600">150,000 emails/month</p>
                                <p className="text-gray-600">£50/month</p>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">Premium Plan:</p>
                                <p className="text-gray-600">5,000 contacts</p>
                                <p className="text-gray-600">30,000 emails/month</p>
                                <p className="text-gray-600">£19/month</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleUpgradeAccount('monthly')}
                              disabled={isUpgrading}
                              className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              {isUpgrading ? 'Processing...' : 'Downgrade to Premium - £19/month'}
                            </button>
                            <p className="mt-2 text-xs text-gray-500">
                              Changes take effect at the end of your current billing period
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                      <button
                        type="button"
                        onClick={handleUpdatePaymentMethod}
                        disabled={loading}
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Update Payment Method
                      </button>
                      
                      {subscription.status === 'active' && !subscription.cancel_at_period_end ? (
                        <button
                          type="button"
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          {isCancelling ? 'Processing...' : 'Cancel Subscription'}
                        </button>
                      ) : subscription.status === 'active' && subscription.cancel_at_period_end ? (
                        <button
                          type="button"
                          onClick={handleReactivateSubscription}
                          disabled={isCancelling}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          {isCancelling ? 'Processing...' : 'Reactivate Subscription'}
                        </button>
                      ) : null}
                    </div>
                    
                    <div className="mt-4 border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-3">Subscription History</h4>
                      <p className="text-sm text-gray-500">
                        To view your complete billing history, update payment methods, or download invoices, 
                        use the Stripe Customer Portal.
                      </p>
                      <button
                        type="button"
                        onClick={handleUpdatePaymentMethod}
                        className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Billing History
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="py-6">
                    <div className="text-center mb-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscription</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        You don't have an active subscription with us yet.
                      </p>
                    </div>
                    
                    {/* Upgrade Account Section */}
                    <div className="bg-white border rounded-lg shadow-sm p-6 max-w-md mx-auto">
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Upgrade Your Account</h4>
                      
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Unlimited business idea generation</p>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Complete business plan creation</p>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Premium features and priority support</p>
                        </div>
                      </div>
                      
                      <div className="mt-2 space-y-3">
                        <button
                          type="button"
                          onClick={() => handleUpgradeAccount('monthly')}
                          disabled={isUpgrading}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isUpgrading ? 'Processing...' : 'Upgrade Monthly - £19/month'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpgradeAccount('annual')}
                          disabled={isUpgrading}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isUpgrading ? 'Processing...' : 'Upgrade Annually - £182.40/year'} {!isUpgrading && <span className="text-xs ml-1 bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">Save 20%</span>}
                        </button>
                        
                        {/* Cold Email Pro Tier */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold text-gray-900">Cold Email Pro</h4>
                              <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                                MOST POPULAR
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-700 mb-4">
                              Perfect for serious cold email campaigns. Scale your outreach with generous limits.
                            </p>
                            
                            <div className="space-y-3 mb-4">
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-purple-600 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700"><strong>30,000 contacts</strong> - 6x more than premium</span>
                              </div>
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-purple-600 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700"><strong>150,000 emails/month</strong> - 5x more sending power</span>
                              </div>
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-purple-600 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">All premium features included</span>
                              </div>
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-purple-600 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">AI-powered inbox features</span>
                              </div>
                              <div className="flex items-start">
                                <svg className="h-5 w-5 text-purple-600 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">Priority email support</span>
                              </div>
                            </div>
                            
                            <button
                              type="button"
                              onClick={() => handleUpgradeAccount('cold_email_pro')}
                              disabled={isUpgrading}
                              className={`w-full inline-flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {isUpgrading ? 'Processing...' : 'Upgrade to Cold Email Pro - £50/month'}
                            </button>
                          </div>
                        </div>
                        
                        {/* Video Credits Section */}
                        <div className="mt-8 pt-5 border-t border-gray-200">
                          <h4 className="text-lg font-medium text-gray-900 mb-3">Video Generation Credits</h4>
                          
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">Current balance:</p>
                            <div className="flex items-center">
                              <span className="text-xl font-bold text-gray-800">
                                {isLoadingCredits ? 'Loading...' : videoCredits !== null ? videoCredits : '-'}
                              </span>
                              <span className="ml-2 text-sm text-gray-500">credits</span>
                            </div>
                          </div>
                          
                          <p className="text-sm text-gray-500 mb-4">
                            Video credits allow you to generate professional quality videos with our AI tools.
                            Different models and video settings use varying amounts of credits.
                          </p>
                          
                          <div className="space-y-3 mt-3">
                            <button
                              type="button"
                              onClick={() => handleBuyCredits('small')}
                              disabled={isUpgrading}
                              className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {isUpgrading ? 'Processing...' : '50 Credits - £10'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleBuyCredits('medium')}
                              disabled={isUpgrading}
                              className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {isUpgrading ? 'Processing...' : '150 Credits - £25'} {!isUpgrading && <span className="text-xs ml-1 bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">Save 16%</span>}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleBuyCredits('large')}
                              disabled={isUpgrading}
                              className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {isUpgrading ? 'Processing...' : '500 Credits - £75'} {!isUpgrading && <span className="text-xs ml-1 bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">Save 25%</span>}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-base font-medium text-gray-900">Change Password</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Update your password to keep your account secure.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleChangePassword}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Change Password
                    </button>
                  </div>
                </div>
                
                <div className="pt-5 border-t border-gray-200">
                  <h4 className="text-base font-medium text-gray-900">Delete Account</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleAccountDeletion}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          

  {/* IMAP Connection Modal */}
  {imapOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
        <form onSubmit={handleSubmitImap}>
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Add IMAP/SMTP Account</h3>
            <p className="text-sm text-gray-500 mt-1">Enter your email provider settings. These are usually available from your provider's help pages.</p>
          </div>
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  className="mt-1 w-full border rounded-md p-2"
                  value={imapForm.email}
                  onChange={(e) => setImapForm({ ...imapForm, email: e.target.value })}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700">Name (optional)</label>
                <input
                  type="text"
                  className="mt-1 w-full border rounded-md p-2"
                  value={imapForm.name}
                  onChange={(e) => setImapForm({ ...imapForm, name: e.target.value })}
                  placeholder="Display name"
                />
              </div>
            </div>

            {/* SMTP Settings */}
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">SMTP (Outgoing Mail)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">SMTP Host</label>
                  <input
                    type="text"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.smtp_host}
                    onChange={(e) => setImapForm({ ...imapForm, smtp_host: e.target.value })}
                    placeholder="smtp.your-provider.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Port</label>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.smtp_port}
                    onChange={(e) => setImapForm({ ...imapForm, smtp_port: Number(e.target.value) || 0 })}
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.smtp_username}
                    onChange={(e) => setImapForm({ ...imapForm, smtp_username: e.target.value })}
                    placeholder="Often your email address"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.smtp_password}
                    onChange={(e) => setImapForm({ ...imapForm, smtp_password: e.target.value })}
                    required
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-4">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={imapForm.smtp_secure}
                      onChange={(e) => setImapForm({ ...imapForm, smtp_secure: e.target.checked })}
                    />
                    Use SSL/TLS (smtps)
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={imapForm.smtp_require_tls}
                      onChange={(e) => setImapForm({ ...imapForm, smtp_require_tls: e.target.checked })}
                    />
                    Require STARTTLS
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={imapForm.smtp_tls_reject_unauthorized}
                      onChange={(e) => setImapForm({ ...imapForm, smtp_tls_reject_unauthorized: e.target.checked })}
                    />
                    Reject invalid TLS certs
                  </label>
                </div>
              </div>
            </div>

            {/* IMAP Settings */}
            <div>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">IMAP (Incoming Mail)</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">IMAP Host</label>
                  <input
                    type="text"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.imap_host}
                    onChange={(e) => setImapForm({ ...imapForm, imap_host: e.target.value })}
                    placeholder="imap.your-provider.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Port</label>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.imap_port}
                    onChange={(e) => setImapForm({ ...imapForm, imap_port: Number(e.target.value) || 0 })}
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <input
                    type="text"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.imap_username}
                    onChange={(e) => setImapForm({ ...imapForm, imap_username: e.target.value })}
                    placeholder="Often your email address"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    className="mt-1 w-full border rounded-md p-2"
                    value={imapForm.imap_password}
                    onChange={(e) => setImapForm({ ...imapForm, imap_password: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
              onClick={() => setImapOpen(false)}
              disabled={imapSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              disabled={imapSubmitting}
            >
              {imapSubmitting ? 'Connecting...' : 'Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )}

</div>
</div>
</div>
</div>
);
};

export default AccountPage;
